import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@ridemaadi/ui';
import { Logo } from '../Logo';

const NAV_LINKS = [
  { to: '/features', label: 'Features' },
  { to: '/how-it-works', label: 'How it works' },
  { to: '/for-riders', label: 'For riders' },
  { to: '/for-riding-groups', label: 'For groups' },
  { to: '/about', label: 'About' },
] as const;

const FOOTER_LINKS = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
  { to: '/about', label: 'About' },
] as const;

function navLinkClass(active: boolean) {
  return active
    ? 'font-medium text-primary'
    : 'text-muted hover:text-primary';
}

export function PublicLayout({
  children,
  onSignIn,
}: {
  children: ReactNode;
  onSignIn: () => void;
}) {
  const { pathname } = useLocation();

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-content">
      <header
        className="sticky top-0 z-10 border-b border-border/60 bg-surface/95 backdrop-blur-md"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="mx-auto flex max-w-app items-center justify-between gap-3 px-4 py-3">
          <Link to="/" className="shrink-0" aria-label="RideMaadi home">
            <Logo variant="header" className="h-[36px] sm:h-[40px]" />
          </Link>
          <nav aria-label="Public" className="hidden items-center gap-5 text-sm lg:flex">
            {NAV_LINKS.map(({ to, label }) => (
              <Link key={to} to={to} className={navLinkClass(pathname === to)}>
                {label}
              </Link>
            ))}
          </nav>
          <Button size="sm" onClick={onSignIn} aria-label="Sign in with Google">
            Sign in
          </Button>
        </div>
        <nav
          aria-label="Public mobile"
          className="mx-auto flex max-w-app gap-4 overflow-x-auto px-4 pb-3 text-xs font-medium lg:hidden"
        >
          {NAV_LINKS.map(({ to, label }) => (
            <Link key={to} to={to} className={`shrink-0 ${navLinkClass(pathname === to)}`}>
              {label}
            </Link>
          ))}
        </nav>
      </header>

      <main className="flex-1">{children}</main>

      <footer
        className="border-t border-border/60 bg-surface px-4 py-8"
        style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto flex max-w-app flex-col gap-6">
          <nav aria-label="Footer" className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
            {FOOTER_LINKS.map(({ to, label }) => (
              <Link key={to} to={to} className="text-muted hover:text-primary">
                {label}
              </Link>
            ))}
          </nav>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted">
              RideMaadi — motorcycle riding and group ride app for riders and communities.
            </p>
            <p className="text-xs text-muted">
              <Link to="/" className="hover:text-primary">
                Home
              </Link>
              {' · '}
              © {new Date().getFullYear()} RideMaadi
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
